import {
  Box,
  Flex,
  Icon,
  Radio,
  RadioGroup,
  Text,
  TextProps,
} from '@everlywell/ui-kit';
import { Link } from '@everlywell/ui-kit';
import { ArrowSquareOut, CheckCircle } from '@phosphor-icons/react';
import { VariantProps } from 'components/OrderSummary';
import { PurchaseOptionsDrawerProduct } from 'components/PurchaseOptionsDrawerProvider/PurchaseOptionsDrawerProvider.context';
import React, { useEffect, useState } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import {
  ELIMINATION_DIET_COACHING_SLUG,
  VITAMINS,
} from 'utils/constants/products';

import PurchaseOption, {
  PriceComponent,
  PurchaseOptionDescription,
  PurchaseOptionHeading,
  PurchaseOptionProps,
} from '../PurchaseOption';
import { MembershipOptionType } from '../PurchaseOptionsDrawer';

export type ProgramMembershipOptionProps = Pick<
  PurchaseOptionProps,
  'id' | 'onSelected'
> & {
  product: PurchaseOptionsDrawerProduct | null | undefined;
  isExpanded: boolean;
  hasCheckoutOptions: boolean;
  onSubscriptionOptionSelect: (option: MembershipOptionType) => void;
};

/**
 * Renders the Everlywell+ Membership option
 */
const ProgramMembershipOption = (
  props: ProgramMembershipOptionProps,
): JSX.Element | null => {
  const [selectedSubscription, setSelectedSubscription] = useState(
    MembershipOptionType.MonthlyMembership,
  );
  const isEliminationDiet =
    props.product?.slug === ELIMINATION_DIET_COACHING_SLUG;

  // List of products with custom Everlywell 360 content
  const everlywell360CustomProducts = ['womens-health-test', 'mens-health-test', 'thyroid-test'];

  const hasCustomContent = props.product?.slug !== undefined && everlywell360CustomProducts.includes(props.product.slug);

  // Check if we're on the men's health test product page
  const isMensHealthTest = props.product?.slug === 'mens-health-test';

  // Check if we're on the women's health test product page
  const isWomensHealthTest = props.product?.slug === 'womens-health-test';

  // Check if we're on the thyroid test product page
  const isThyroidTest = props.product?.slug === 'thyroid-test';

  // Use custom copy for men's, women's, or thyroid health test, default copy for all others
  const biomarkerText = hasCustomContent && (isMensHealthTest || isWomensHealthTest || isThyroidTest)
    ? 'Go beyond your hormones. See your whole health'
    : 'The most advanced test we\'ve ever created.';

  /**
   * TODO: Update to receive this data from Contentful instead
   */
  const mainOptionPrice: Record<
    MembershipOptionType,
    { amount: number; unitLabel: string }
  > = {
    [MembershipOptionType.MonthlyMembership]: {
      amount: 33.25, // Always use Everlywell 360 pricing
      unitLabel: '/per month, $399 billed annually',
    },
    [MembershipOptionType.YearlyMembership]: {
      amount: 449,
      unitLabel: '/year',
    },
    [MembershipOptionType.WeightCareMembership]: {
      amount: 49,
      unitLabel: '/month',
    },
  };

  useEffect(() => {
    if (!props.isExpanded) {
      setSelectedSubscription(MembershipOptionType.MonthlyMembership);
    }
  }, [props.isExpanded]);

  // If the product is not available or the subscription is hidden, we don't render the component
  if (
    !props.product ||
    VITAMINS.includes(props.product.slug) ||
    isEliminationDiet
  ) {
    return null;
  }

  const { hasCheckoutOptions, onSubscriptionOptionSelect } = props;

  const trackLearnMoreClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_LINK,
      data: {
        label: 'Learn more',
        component: props.id,
        experience: 'Pre-Cart',
        'Product Name': props.product?.name || '',
      },
    });
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        paddingBottom={3}
      >
        <Text fontSize="lg">
          {props.product?.slug && ['womens-health-test', 'mens-health-test', 'thyroid-test'].includes(props.product.slug) 
            ? 'Save with Everlywell 360' 
            : 'Discover more with Everlywell 360'}
        </Text>
        <Link
          flexShrink={0}
          href="/360-full-body-test/"
          display="flex"
          alignItems="center"
          gap={1}
          isExternal
          onClick={trackLearnMoreClick}
        >
          {/** TODO: Should Link sizes extend from Text sizes config? */}
          <Text fontSize="lg" as="span">
            Learn more
          </Text>
          <Icon as={ArrowSquareOut} boxSize={20} />
        </Link>
      </Box>
      <PurchaseOption
        id={props.id}
        onSelected={props.onSelected}
        heading={
          <PurchaseOptionHeading>
            Everlywell 360
          </PurchaseOptionHeading>
        }
        disableOnSelect={hasCheckoutOptions && props.isExpanded}
        description={
          <PurchaseOptionDescription>
            {hasCheckoutOptions ? (
              <Box as="span" color="eggplant.base">
                {biomarkerText}
              </Box>
            ) : (
              <Box as="span" color="utility.sale">
                {biomarkerText}
              </Box>
            )}
          </PurchaseOptionDescription>
        }
        price={mainOptionPrice[selectedSubscription]}
      >
        {hasCheckoutOptions ? (
          <SubscriptionOptions
            selectedSubscription={selectedSubscription}
            onSelect={(option) => {
              setSelectedSubscription(option as MembershipOptionType);
              onSubscriptionOptionSelect(option);
            }}
            isEWPlus={true}
          />
        ) : (
          <PurchaseOptionDescription
            color="tints.darkGrey"
            display="flex"
            flexDirection="column"
            gap={4}
            as="div"
          >
            {/* Always show Everlywell 360 content */}
            <>
              Everlywell 360 gives you unparalleled insight into your health with 83 biomarkers assessed from one lab visit. From hormones to heart health, this is the future of personalized wellness.
              <Box
                as="ul"
                color="tints.black"
                display="flex"
                flexDirection="column"
                gap={4}
              >
                <ListItem>One 83-biomarker lab test, every year</ListItem>
                <ListItem>Personalized, clinician-reviewed Care Plan</ListItem>
                <ListItem>FSA/HSA eligible, no hidden costs</ListItem>
              </Box>
            </>
          </PurchaseOptionDescription>
        )}
      </PurchaseOption>
    </Box>
  );
};

export default ProgramMembershipOption;

const ListItem = (props: TextProps) => (
  <Text
    as="li"
    fontSize="inherit"
    color="inherit"
    display="flex"
    alignItems="center"
    gap={1}
  >
    <Icon as={CheckCircle} /> {props.children}
  </Text>
);

export type SubscriptionOptionsProps = {
  selectedSubscription: MembershipOptionType;
  onSelect: (option: MembershipOptionType) => void;
  variants?: VariantProps[];
  isEWPlus?: boolean;
};

export const SubscriptionOptions = ({
  selectedSubscription,
  onSelect,
  variants,
  isEWPlus,
}: SubscriptionOptionsProps): JSX.Element => {
  useEffect(() => {
    onSelect(selectedSubscription);
  }, [selectedSubscription, onSelect]);

  if (isEWPlus) {
    const monthlyPrice = variants?.[0]?.total || '';
    const yearlyPrice = variants?.[1]?.total || '';

    return (
      <RadioGroup
        onChange={(option) => {
          onSelect(option as unknown as MembershipOptionType);
        }}
        value={selectedSubscription as unknown as string}
        display="flex"
        flexDirection="column"
        gap="3"
      >
        <Radio
          value={MembershipOptionType.YearlyMembership as unknown as string}
          data-testid={MembershipOptionType.YearlyMembership}
        >
          <Flex justifyContent="space-between" flex="1" gap="2">
            <Box>
              <PurchaseOptionHeading>
                Pay for 12 months upfront
              </PurchaseOptionHeading>
              <PurchaseOptionDescription>
                <Box as="span" color="tints.darkGrey">
                  Commit to a year of taking care of your wellness for only $
                  {yearlyPrice.split('.')[0]} billed annually and{' '}
                  <Text as="span" color="eggplant.base">
                    enjoy 2 bonus credits.
                  </Text>
                </Box>
              </PurchaseOptionDescription>
            </Box>
            <Box>
              <PriceComponent
                dollars={yearlyPrice.split('.')[0]}
                cents="00"
                unitLabel="/year"
              ></PriceComponent>
            </Box>
          </Flex>
        </Radio>
        <Radio
          value={MembershipOptionType.MonthlyMembership as unknown as string}
          data-testid={MembershipOptionType.MonthlyMembership}
        >
          <Flex justifyContent="space-between" flex="1" gap="1">
            <Box>
              <PurchaseOptionHeading>Pay monthly</PurchaseOptionHeading>
              <PurchaseOptionDescription>
                <Box as="span" color="tints.darkGrey">
                  Earn one credit per month, cancel any time
                </Box>
              </PurchaseOptionDescription>
            </Box>
            <Box>
              <PriceComponent
                dollars={monthlyPrice.split('.')[0]}
                cents="00"
                unitLabel="/month"
              ></PriceComponent>
            </Box>
          </Flex>
        </Radio>
      </RadioGroup>
    );
  } else {
    const variantOptions = variants?.map((variant, index: number) => {
      // Allow empty frequency for complex plan options. If frequency not set, default to month.
      const frequencyUnitLabel =
        typeof variant.frequency === 'string'
          ? variant.frequency.length === 0
            ? variant.frequency
            : `/${variant.frequency}`
          : '/month';

      return (
        <Radio
          key={`radio-button-${index}`}
          value={variant.slug as unknown as string}
          data-testid={variant.slug}
        >
          <Flex justifyContent="space-between" flex="1" gap="2">
            <Box>
              <PurchaseOptionHeading>{variant.heading}</PurchaseOptionHeading>

              <PurchaseOptionDescription>
                <Box as="span" color="tints.darkGrey">
                  {variant.description}
                </Box>
              </PurchaseOptionDescription>
            </Box>

            <Box>
              <PriceComponent
                dollars={variant.total.split('.')[0]}
                cents="00"
                unitLabel={frequencyUnitLabel}
              ></PriceComponent>
            </Box>
          </Flex>
        </Radio>
      );
    });

    return (
      <RadioGroup
        onChange={(option) => {
          onSelect(option as unknown as MembershipOptionType);
        }}
        value={selectedSubscription as unknown as string}
        display="flex"
        flexDirection="column"
        gap="3"
      >
        {variantOptions}
      </RadioGroup>
    );
  }
};
